import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { buildItemUrl } from "../utils/itemUrl";
import usePageTitle from "../hooks/usePageTitle";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  CircularProgress,
  Avatar,
  Paper,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Chip,
  Checkbox,
  Link,
  Menu,
  Snackbar,
  SvgIcon,
} from "@mui/material";
import { api, getAuth } from "../api";
import { formatPrice, getPriceDollars } from "../utils/price";
import { usePriceVisibility } from "../contexts/PriceVisibilityContext";
import { getDisplayImageUrl } from "../utils/imageUrl";
import { toDisplayImageUrl } from "../utils/imageUrl";
import { jsPDF } from "jspdf";
import PaginationControls from "./PaginationControls";
import PriceConfidenceRibbon from "./PriceConfidenceRibbon";

const normalizeConfidence = (value) => {
  const v = String(value ?? "").trim().toLowerCase();

  if (
    [
      "high",
      "h",
      "strong",
      "high_active_exact",
      "high_active",
      "high_confidence",
    ].includes(v)
  ) {
    return "high";
  }

  if (
    [
      "medium",
      "med",
      "mid",
      "m",
      "medium_active_exact",
      "medium_active",
      "manual_sold_comp",
      "verified_manual_comp",
      "carry_forward_verified",
      "stale_carry_forward",
    ].includes(v)
  ) {
    return "medium";
  }

  if (
    [
      "low",
      "l",
      "weak",
      "low_active_adjusted",
      "low_active",
      "carry_forward",
      "carry_forward_recent",
      "carry_forward_infra_safe",
      "estimate_low",
      "category_median_fallback",
      "category_only_median_fallback",
      "item_type_median_fallback",
      "multi_source_weighted",
    ].includes(v)
  ) {
    return "low";
  }

  return "";
};

const withEbaySoldCompleted = (url) => {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes("ebay.")) return url;
    parsed.searchParams.set("LH_Sold", "1");
    parsed.searchParams.set("LH_Complete", "1");
    return parsed.toString();
  } catch {
    return url;
  }
};

const withEpnTracking = (url, customId) => {
  if (!url) return "";

  const campid = String(process.env.REACT_APP_EPN_CAMPID || "").trim();
  const mkrid = String(process.env.REACT_APP_EPN_MKRID || "").trim();
  const toolid = String(process.env.REACT_APP_EPN_TOOLID || "").trim();
  const siteid = String(process.env.REACT_APP_EPN_SITEID || "").trim();

  try {
    const parsed = new URL(url);
    parsed.searchParams.set("mkcid", "1");
    parsed.searchParams.set("mkrid", mkrid);
    parsed.searchParams.set("siteid", siteid);
    parsed.searchParams.set("campid", campid);
    parsed.searchParams.set("customid", String(customId || ""));
    parsed.searchParams.set("toolid", toolid);
    parsed.searchParams.set("mkevt", "1");
    return parsed.toString();
  } catch {
    return url;
  }
};

const buildEbaySearchUrl = (queryText, { includeSoldCompleted = false, sortNewest = false } = {}) => {
  const text = String(queryText || "").trim();
  if (!text) return "";
  const query = encodeURIComponent(text);
  const url = new URL(`https://www.ebay.com/sch/i.html?_nkw=${query}`);

  if (includeSoldCompleted) {
    url.searchParams.set("LH_Sold", "1");
    url.searchParams.set("LH_Complete", "1");
  }
  if (sortNewest) {
    url.searchParams.set("_sop", "10");
  }

  return url.toString();
};

const PLACEHOLDER_TOKENS = new Set(["n/a", "unknown", "none", "null", "undefined", "misc", "for"]);

const cleanField = (value) => {
  if (value == null) return "";
  const text = String(value).replace(/\s+/g, " ").trim();
  if (!text) return "";
  if (PLACEHOLDER_TOKENS.has(text.toLowerCase())) return "";
  return text;
};

const toShopEbayUrl = (item, ebayQueryText, index = "") => {
  const query = cleanField(ebayQueryText);
  if (!query) return "";
  const baseUrl = buildEbaySearchUrl(query, { sortNewest: true });
  return withEpnTracking(baseUrl, `mycollection_shop_${item?.user_item_id ?? item?.item_id ?? index}`);
};

const trackOutbound = (item, destination, url) => {
  if (!url) return;
  const trackingItemId = Number(item?.item_id ?? item?.user_item_id ?? 0);
  if (!Number.isFinite(trackingItemId) || trackingItemId <= 0) return;

  void api.post("/api/track/outbound", {
    item_id: trackingItemId,
    destination,
    url,
    ts: new Date().toISOString(),
  }).catch(() => {});
};

const openOutbound = (item, destination, url) => {
  if (!url) return;
  trackOutbound(item, destination, url);
  window.open(url, "_blank", "noopener,noreferrer");
};

function formatDate(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (!Number.isFinite(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const normalizeSubcategoryId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const buildSubcategoryTree = (items = []) => {
  const nodes = items
    .map((item) => ({
      ...item,
      subcategory_id: normalizeSubcategoryId(item.subcategory_id),
      parent_subcategory_id: normalizeSubcategoryId(item.parent_subcategory_id),
      children: [],
    }))
    .filter((item) => item.subcategory_id != null);
  const byId = new Map(nodes.map((node) => [node.subcategory_id, node]));
  const roots = [];

  nodes.forEach((node) => {
    const parentId = node.parent_subcategory_id;
    if (parentId != null && byId.has(parentId)) {
      byId.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (list) => {
    list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    list.forEach((node) => {
      if (node.children.length > 0) {
        sortNodes(node.children);
      }
    });
  };

  sortNodes(roots);
  return roots;
};

const getSubcategoryScopeIds = (items, selectedValue) => {
  const selectedId = normalizeSubcategoryId(selectedValue);
  if (selectedId == null) return null;
  const normalized = items
    .map((item) => ({
      ...item,
      subcategory_id: normalizeSubcategoryId(item.subcategory_id),
      parent_subcategory_id: normalizeSubcategoryId(item.parent_subcategory_id),
    }))
    .filter((item) => item.subcategory_id != null);
  const byId = new Map(normalized.map((item) => [item.subcategory_id, item]));
  const selected = byId.get(selectedId);
  if (!selected) return null;
  if (selected.parent_subcategory_id != null) {
    return [selectedId];
  }
  const childIds = normalized
    .filter((item) => item.parent_subcategory_id === selectedId)
    .map((item) => item.subcategory_id);
  return [selectedId, ...childIds];
};
const normalizeDisplayLabel = (value) => String(value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
const renderSubcategoryChips = (item = {}) => {
  // A category is not a subcategory. Only ownership rows with an actual
  // assigned subcategory may contribute labels to this display path.
  if (normalizeSubcategoryId(item.subcategory_id) == null) return null;
  const category = normalizeDisplayLabel(item.category);
  const parent = item.parent_subcategory_name;
  const child = item.subcategory_name;

  const chips = [];
  const seen = new Set();
  const addChip = (value, key) => {
    const text = String(value || "").trim();
    const normalized = normalizeDisplayLabel(text);
    if (!text || normalized === category || seen.has(normalized)) return;
    seen.add(normalized);
    chips.push({ key, text });
  };

  addChip(parent, "parent");
  if (child) addChip(child, "child");

  if (chips.length === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.6,
        flexWrap: "wrap",
        mt: 0.5,
        minHeight: 28,
        alignContent: "flex-start",
        width: "100%",
        overflow: "hidden",
      }}
    >
      {chips.map(({ key, text }) => (
      <Chip
        key={key}
        label={text}
        size="small"
        variant="outlined"
        sx={{
          fontWeight: 600,
          minHeight: 24,
          height: "auto",
          maxWidth: "100%",
          borderRadius: "999px",
          bgcolor: "rgba(241, 245, 249, 0.9)",
          color: "text.secondary",
          borderColor: "rgba(100, 116, 139, 0.28)",
          boxShadow: "none",
          fontSize: "0.72rem",
          "& .MuiChip-label": { whiteSpace: "normal", overflowWrap: "anywhere", py: 0.35 },
        }}
      />
      ))}
    </Box>
  );
};
const OWNERSHIP_STATUS_OPTIONS = [["favorite", "Favorite", "is_favorite"], ["for_trade", "For Trade", "is_for_trade"], ["for_sale", "For Sale", "is_for_sale"], ["locked", "Locked", "is_locked"]];
const statusActive = (item, key) => Number(item?.[`is_${key}`]) === 1;
const statusChipSx = {
  favorite: { bgcolor: "rgba(245, 158, 11, 0.10)", color: "#92400e", borderColor: "rgba(245, 158, 11, 0.35)" },
  for_trade: { bgcolor: "rgba(13, 148, 136, 0.10)", color: "#0f766e", borderColor: "rgba(13, 148, 136, 0.35)" },
  for_sale: { bgcolor: "rgba(22, 163, 74, 0.10)", color: "#166534", borderColor: "rgba(22, 163, 74, 0.35)" },
  locked: { bgcolor: "rgba(71, 85, 105, 0.10)", color: "#334155", borderColor: "rgba(71, 85, 105, 0.35)" },
};

const SearchIcon = (props) => (
  <SvgIcon {...props}><path d="M9.5 3a6.5 6.5 0 1 0 3.98 11.64L18.85 20 20 18.85l-5.36-5.37A6.5 6.5 0 0 0 9.5 3Zm0 2a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Z" /></SvgIcon>
);
const ScanIcon = (props) => (
  <SvgIcon {...props}><path d="M4 4h5V2H4a2 2 0 0 0-2 2v5h2V4Zm16-2h-5v2h5v5h2V4a2 2 0 0 0-2-2Zm0 18h-5v2h5a2 2 0 0 0 2-2v-5h-2v5ZM4 15H2v5a2 2 0 0 0 2 2h5v-2H4v-5Zm2-7h12v8H6V8Zm2 2v4h8v-4H8Z" /></SvgIcon>
);
const SparkIcon = (props) => (
  <SvgIcon {...props}><path d="m12 2 1.55 4.45L18 8l-4.45 1.55L12 14l-1.55-4.45L6 8l4.45-1.55L12 2Zm6 11 .9 2.1L21 16l-2.1.9L18 19l-.9-2.1L15 16l2.1-.9L18 13ZM6 13l1.15 2.85L10 17l-2.85 1.15L6 21l-1.15-2.85L2 17l2.85-1.15L6 13Z" /></SvgIcon>
);
const EditIcon = (props) => (
  <SvgIcon {...props}><path d="M4 17.25V21h3.75L18.81 9.94l-3.75-3.75L4 17.25Zm16.71-10.12a1 1 0 0 0 0-1.41l-2.43-2.43a1 1 0 0 0-1.41 0l-1.9 1.9 3.75 3.75 1.99-1.81Z" /></SvgIcon>
);

function FirstCollectionOnboarding({ navigate }) {
  const actions = [
    { title: "Search the Database", description: "Find collectibles that are already available and add them to your collection.", label: "Search Items", route: "/search?q=__all__", Icon: SearchIcon, recommended: true },
    { title: "Scan an Item", description: "Use your camera to identify an item and look for an exact database match.", label: "Open Scanner", action: () => window.dispatchEvent(new CustomEvent("tcs:open-scan")), Icon: ScanIcon },
    { title: "Create with AI", description: "Generate a missing collectible with AI assistance and submit it for review.", label: "Create with AI", route: "/search?create=ai", Icon: SparkIcon },
    { title: "Add Manually", description: "Enter the collectible details yourself when you already know what to add.", label: "Add Manually", route: "/add", Icon: EditIcon },
  ];

  return (
    <Paper component="section" aria-labelledby="first-collection-heading" elevation={0} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 4, border: "1px solid #dbe7f5", background: "linear-gradient(145deg, #f7fbff 0%, #eef5fd 100%)", boxShadow: "0 14px 34px rgba(15, 39, 71, 0.08)" }}>
      <Typography variant="overline" sx={{ color: "#235fa4", fontWeight: 800, letterSpacing: 1.2 }}>Your collection starts here</Typography>
      <Typography id="first-collection-heading" component="h2" variant="h4" sx={{ color: "#10233f", fontWeight: 800, mt: 0.25 }}>Add your first collectible</Typography>
      <Typography sx={{ color: "#42566f", mt: 1, maxWidth: 680 }}>Search the database, scan an item, or create something that is not listed yet.</Typography>
      <Typography variant="body2" sx={{ color: "#52677f", fontWeight: 700, mt: 1 }}>No subscription required.</Typography>

      <Grid container spacing={2} sx={{ mt: 1.5 }}>
        {actions.map(({ title, description, label, route, action, Icon, recommended }) => (
          <Grid item xs={12} sm={6} lg={3} key={title}>
            <Paper variant="outlined" sx={{ height: "100%", p: 2.25, borderRadius: 3, display: "flex", flexDirection: "column", bgcolor: "#fff", borderColor: recommended ? "#78aee8" : "#dce5ef", boxShadow: recommended ? "0 8px 22px rgba(35,95,164,.12)" : "none", transition: "transform 160ms ease, box-shadow 160ms ease", "&:hover": { transform: "translateY(-2px)", boxShadow: "0 10px 24px rgba(15,39,71,.12)" }, "&:focus-within": { outline: "3px solid rgba(25,118,210,.28)", outlineOffset: 2 } }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.5 }}>
                <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: recommended ? "#e3f0ff" : "#edf3f9", color: "#235fa4", display: "grid", placeItems: "center" }}><Icon aria-hidden="true" /></Box>
                {recommended && <Chip label="Recommended" size="small" color="primary" sx={{ fontWeight: 700 }} />}
              </Box>
              <Typography component="h3" variant="h6" sx={{ color: "#10233f", fontWeight: 800, fontSize: "1.05rem" }}>{title}</Typography>
              <Typography variant="body2" sx={{ color: "#52677f", mt: 0.75, mb: 2, lineHeight: 1.55 }}>{description}</Typography>
              <Button variant={recommended ? "contained" : "outlined"} onClick={() => action ? action() : route && navigate(route)} aria-label={title === "Scan an Item" ? "Open scanner" : label} sx={{ mt: "auto", minHeight: 44, fontWeight: 700 }}>{label}</Button>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ mt: 3, p: { xs: 2, sm: 2.5 }, borderRadius: 3, bgcolor: "rgba(255,255,255,.78)", borderColor: "#dce5ef" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography component="h3" variant="h6" sx={{ color: "#10233f", fontWeight: 800 }}>Already have a collection somewhere else?</Typography>
          <Chip label="Collection Import Coming Soon" size="small" variant="outlined" color="primary" />
        </Box>
        <Typography variant="body2" sx={{ color: "#52677f", mt: 0.75 }}>Collection import is coming soon. We are planning support for spreadsheets and other collection trackers.</Typography>
        <Box aria-label="Planned collection import flow" sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mt: 2, color: "#284766" }}>
          {['Spreadsheet', 'Collection App', 'The Collector Stop'].map((step, index) => <React.Fragment key={step}><Chip label={step} sx={{ bgcolor: "#edf4fb", fontWeight: 700 }} />{index < 2 && <Typography aria-hidden="true">→</Typography>}</React.Fragment>)}
        </Box>
      </Paper>

      <Box sx={{ mt: 3 }}>
        <Typography component="h3" variant="subtitle1" sx={{ color: "#10233f", fontWeight: 800 }}>Need some inspiration?</Typography>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, flexWrap: "wrap", gap: 1, mt: 1 }}>
          <Button variant="text" onClick={() => navigate('/search?q=__all__')} sx={{ minHeight: 44 }}>Browse Categories</Button>
          <Button variant="text" onClick={() => navigate('/sets/all')} sx={{ minHeight: 44 }}>Browse Sets</Button>
          <Button variant="text" onClick={() => navigate('/')} sx={{ minHeight: 44 }}>View Trending Items</Button>
        </Box>
      </Box>
    </Paper>
  );
}

// Read user from storage
const MyCollection = () => {
  const { showPrices: pricePreferenceEnabled, pricesLoaded } = usePriceVisibility();
  const showPrices = pricesLoaded && pricePreferenceEnabled;
  usePageTitle("My Collection");
  const navigate = useNavigate();
  const { username: routeUsername } = useParams();

  const [collection, setCollection] = useState([]);
  const [collectionSummary, setCollectionSummary] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState("all"); // string: "all" or id as string
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [privateAccount, setPrivateAccount] = useState(false);

  const [currentUser, setCurrentUser] = useState(null);
  const [viewUsername, setViewUsername] = useState("");
  const [viewedUser, setViewedUser] = useState(null);

  const [sortItemOption, setSortItemOption] = useState(() => { const shared = localStorage.getItem("collection_sort_preference"); const legacy = localStorage.getItem("manage_sort_preference"); const value = [shared, legacy].find((entry) => ["high", "low", "az", "za", "newest", "oldest"].includes(entry)) || "high"; localStorage.setItem("collection_sort_preference", value); return value; });
  const [currentPage, setCurrentPage] = useState(1);
  const [shareAnchor, setShareAnchor] = useState(null);
  const [shareNotice, setShareNotice] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedOwnershipIds, setSelectedOwnershipIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkNotice, setBulkNotice] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [organizeAnchor, setOrganizeAnchor] = useState(null);
  const handleSortPreferenceChange = (value) => { setSortItemOption(value); localStorage.setItem("collection_sort_preference", value); sessionStorage.setItem("collection_sort_preference", value); setCurrentPage(1); };
  const isMobile = useMediaQuery("(max-width:600px)");

  const copyCollectionLink = async () => {
    const url = `${window.location.origin}/mycollection/${encodeURIComponent(viewUsername)}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareNotice("Link copied");
    } catch {
      setShareNotice("Couldn’t copy link");
    }
    setShareAnchor(null);
    window.setTimeout(() => setShareNotice(""), 2200);
  };

  /* Legacy hand-written PDF generator retained only in history; the single
     authoritative implementation below uses jsPDF. */
  /* const downloadCollectionPdfLegacy = async () => {
    const exportItems = [...sortedCollection];
    setPdfLoading(true);
    setShareAnchor(null);
    try {
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth(); const pageHeight = doc.internal.pageSize.getHeight();
      const leftMargin = 14; const rightMargin = 14; const topMargin = 16; const bottomMargin = 16; const footerHeight = 8; const columnGap = 6;
      const cardWidth = (pageWidth - leftMargin - rightMargin - columnGap) / 2; const imageBox = 22; const cardPadding = 5;
      const loadImage = async (item) => { try { const url = toDisplayImageUrl(item.image_url || item.ref_image_url); if (!url) return null; const response = await fetch(url); if (!response.ok) return null; const blob = await response.blob(); return await new Promise((resolve) => { const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.onerror = () => resolve(null); reader.readAsDataURL(blob); }); } catch { return null; } };
      const images = await Promise.all(exportItems.map((item) => loadImage(item)));
      const cards = collection.map((item, index) => ({ item, image: images[index], name: getDisplayName(item), fields: [item.category, [item.set_name || item.series_collection, item.number && `#${item.number}`].filter(Boolean).join(" · "), item.edition_variant, showPrices ? `Value: ${formatPrice(item) || "TBD"}` : ""].filter(Boolean) }));
      let currentY = topMargin; let pageNumber = 1;
      const footer = () => { doc.setFontSize(8); doc.setTextColor(120); doc.text(`The Collector Stop · Page ${pageNumber}`, leftMargin, pageHeight - 8); doc.setTextColor(0); };
      const header = () => { doc.setFontSize(18); doc.text(`@${viewUsername}'s Collection`, leftMargin, currentY); currentY += 8; doc.setFontSize(11); doc.text(`${collection.length} Items${showPrices ? ` · Total Value: ${formatPrice({ price: getFilteredTotalValue().toFixed(2) }) || "TBD"}` : ""}`, leftMargin, currentY); currentY += 6; doc.setFontSize(9); doc.setTextColor(100); doc.text(`Generated ${new Date().toLocaleDateString()}`, leftMargin, currentY); doc.setTextColor(0); currentY += 5; doc.setDrawColor(210); doc.line(leftMargin, currentY, pageWidth - rightMargin, currentY); currentY += 8; };
      header();
      for (let index = 0; index < cards.length; index += 2) {
        const pair = cards.slice(index, index + 2); const measured = pair.map((card) => { const textWidth = cardWidth - cardPadding * 2 - imageBox - 4; const title = doc.splitTextToSize(card.name, textWidth).slice(0, 3); const fields = card.fields.flatMap((field) => doc.splitTextToSize(field, textWidth).slice(0, 2)); return { card, title, fields, height: Math.max(imageBox, 7 + title.length * 5 + fields.length * 4) + cardPadding * 2 }; }); const rowHeight = Math.min(Math.max(...measured.map((entry) => entry.height)), 62);
        if (currentY + rowHeight > pageHeight - bottomMargin - footerHeight) { footer(); doc.addPage(); pageNumber += 1; currentY = topMargin; }
        measured.forEach((entry, column) => { const x = leftMargin + column * (cardWidth + columnGap); doc.setFillColor(255, 255, 255); doc.setDrawColor(220); doc.roundedRect(x, currentY, cardWidth, rowHeight, 2, 2, "FD"); if (entry.card.image) { try { doc.addImage(entry.card.image, entry.card.image.startsWith("data:image/png") ? "PNG" : "JPEG", x + cardPadding, currentY + cardPadding, imageBox, imageBox, undefined, "FAST"); } catch {} } doc.setFontSize(11); doc.setTextColor(25); doc.text(entry.title, x + cardPadding + imageBox + 4, currentY + cardPadding + 5); doc.setFontSize(8.5); doc.setTextColor(85); let textY = currentY + cardPadding + 11; entry.fields.forEach((line) => { doc.text(line, x + cardPadding + imageBox + 4, textY); textY += 4; }); doc.setTextColor(0); });
        currentY += rowHeight + 6;
      }
      footer();
      doc.save(`${String(viewUsername || "Collection").replace(/[^a-z0-9_-]+/gi, "-")}-TCS-Collection.pdf`);
    } catch { setShareNotice("Couldn’t create PDF. Please try again."); window.setTimeout(() => setShareNotice(""), 2200); }
    finally { setPdfLoading(false); }
  };

  */
  const downloadCollectionPdf = async () => {
    const exportItems = [...sortedCollection];
    setPdfLoading(true);
    setShareNotice("");
    setShareAnchor(null);
    try {
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const leftMargin = 14;
      const rightMargin = 14;
      const topMargin = 16;
      const bottomMargin = 16;
      const footerHeight = 8;
      const columnGap = 6;
      const cardWidth = (pageWidth - leftMargin - rightMargin - columnGap) / 2;
      const imageBox = 22;
      const cardPadding = 5;
      const textGap = 4;
      const titleLineHeight = 5;
      const metadataLineHeight = 4;
      const titleMetadataGap = 2;
      const fieldGap = 1.5;
      const textWidth = cardWidth - cardPadding * 2 - imageBox - textGap;
      const imageCache = new Map();
      const loadImage = async (item) => {
        try {
          const url = toDisplayImageUrl(item.image_url || item.ref_image_url);
          if (!url) return null;
          if (!imageCache.has(url)) {
            imageCache.set(url, (async () => {
              const response = await fetch(url);
              if (!response.ok) return null;
              const blob = await response.blob();
              return await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result));
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
              });
            })().catch(() => null));
          }
          return await imageCache.get(url);
        } catch {
          return null;
        }
      };
      const truncateLines = (value, width, maxLines, fontSize) => {
        doc.setFontSize(fontSize);
        const lines = doc.splitTextToSize(String(value || ""), width);
        if (lines.length <= maxLines) return lines;
        const result = lines.slice(0, maxLines);
        let lastLine = result[maxLines - 1] || "";
        while (lastLine && doc.getTextWidth(`${lastLine}...`) > width) lastLine = lastLine.slice(0, -1);
        result[maxLines - 1] = `${lastLine.replace(/\s+$/, "")}...`;
        return result;
      };
      const drawUnavailableImage = (x, y) => {
        doc.setFillColor(245, 246, 248);
        doc.setDrawColor(220);
        doc.roundedRect(x, y, imageBox, imageBox, 1.5, 1.5, "FD");
        doc.setFontSize(6.5);
        doc.setTextColor(135);
        doc.text(["Image", "unavailable"], x + imageBox / 2, y + imageBox / 2 - 1, { align: "center" });
        doc.setTextColor(0);
      };
      const images = await Promise.all(exportItems.map((item) => loadImage(item)));
      const cards = exportItems.map((item, index) => ({
        item,
        image: images[index],
        name: getDisplayName(item),
        fields: [
          item.category,
          [item.set_name || item.series_collection, item.number && `#${item.number}`].filter(Boolean).join(" · "),
          item.edition_variant,
          showPrices ? `Value: ${formatPrice(item) || "TBD"}` : "",
        ].filter(Boolean),
      }));
      let currentY = topMargin;
      let pageNumber = 1;
      const footer = () => {
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text(`The Collector Stop · Page ${pageNumber}`, leftMargin, pageHeight - 8);
        doc.setTextColor(0);
      };
      doc.setFontSize(18);
      doc.text(`@${viewUsername}'s Collection`, leftMargin, currentY);
      currentY += 8;
      doc.setFontSize(11);
      doc.text(`${exportItems.length} Items${showPrices ? ` · Total Value: ${formatPrice({ price: getFilteredTotalValue().toFixed(2) }) || "TBD"}` : ""}`, leftMargin, currentY);
      currentY += 6;
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(`Generated ${new Date().toLocaleDateString()}`, leftMargin, currentY);
      currentY += 4;
      doc.setFontSize(7.5);
      doc.setTextColor(125);
      doc.text("Some item images may be unavailable in the exported PDF due to external image restrictions.", leftMargin, currentY);
      doc.setTextColor(0);
      currentY += 4;
      doc.setDrawColor(210);
      doc.line(leftMargin, currentY, pageWidth - rightMargin, currentY);
      currentY += 8;

      for (let index = 0; index < cards.length; index += 2) {
        const measured = cards.slice(index, index + 2).map((card) => {
          const title = truncateLines(card.name, textWidth, 3, 11);
          const fieldLines = card.fields.map((field) => truncateLines(field, textWidth, 2, 8.5));
          const textHeight = title.length * titleLineHeight + titleMetadataGap
            + fieldLines.reduce((height, lines) => height + lines.length * metadataLineHeight + fieldGap, 0);
          return { card, title, fieldLines, height: cardPadding * 2 + Math.max(imageBox, textHeight) };
        });
        const rowHeight = Math.max(...measured.map((entry) => entry.height));
        if (currentY + rowHeight > pageHeight - bottomMargin - footerHeight) {
          footer();
          doc.addPage();
          pageNumber += 1;
          currentY = topMargin;
        }
        for (const [column, entry] of measured.entries()) {
          const x = leftMargin + column * (cardWidth + columnGap);
          doc.setFillColor(255, 255, 255);
          doc.setDrawColor(220);
          doc.roundedRect(x, currentY, cardWidth, rowHeight, 2, 2, "FD");
          if (entry.card.image) {
            try {
              doc.addImage(entry.card.image, entry.card.image.startsWith("data:image/png") ? "PNG" : "JPEG", x + cardPadding, currentY + cardPadding, imageBox, imageBox, undefined, "FAST");
            } catch {
              drawUnavailableImage(x + cardPadding, currentY + cardPadding);
            }
          } else {
            drawUnavailableImage(x + cardPadding, currentY + cardPadding);
          }
          const textX = x + cardPadding + imageBox + textGap;
          let textY = currentY + cardPadding + titleLineHeight;
          doc.setFontSize(11);
          doc.setTextColor(25);
          doc.text(entry.title, textX, textY);
          textY += entry.title.length * titleLineHeight + titleMetadataGap;
          doc.setFontSize(8.5);
          doc.setTextColor(85);
          for (const lines of entry.fieldLines) {
            doc.text(lines, textX, textY);
            textY += lines.length * metadataLineHeight + fieldGap;
          }
          doc.setTextColor(0);
        }
        currentY += rowHeight + 6;
      }
      footer();
      doc.save(`${String(viewUsername || "Collection").replace(/[^a-z0-9_-]+/gi, "-")}-TCS-Collection.pdf`);
    } catch {
      setShareNotice("Couldn’t create PDF. Please try again.");
      window.setTimeout(() => setShareNotice(""), 2200);
    } finally {
      setPdfLoading(false);
    }
  };

  const itemsPerPage = 12;
  const subcategoryTree = useMemo(() => buildSubcategoryTree(subcategories), [subcategories]);
  const subcategoryScopeIds = useMemo(
    () => getSubcategoryScopeIds(subcategories, selectedSubcategory),
    [subcategories, selectedSubcategory]
  );

  // ───────────────── Resolve user + route ─────────────────
  useEffect(() => {
    const { user } = getAuth();
    setCurrentUser(user || null);

    if (!routeUsername) {
      const uname = user?.username || user?.name;
      if (uname) {
        navigate(`/mycollection/${encodeURIComponent(uname)}`, {
          replace: true,
        });
      } else {
        setViewUsername("");
        setViewedUser(null);
        return;
      }
      return;
    }
    setViewUsername(routeUsername);
  }, [routeUsername, navigate]);

  // ───────────────── Load subcategories from backend ─────────────────
  useEffect(() => {
    if (!viewUsername) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await api.get(
          `/api/subcategories/user/${encodeURIComponent(viewUsername)}`
        );
        if (!cancelled) {
          const list = Array.isArray(res.data) ? res.data : [];
          setSubcategories(
            list.map((s) => ({
              ...s,
              subcategory_id: normalizeSubcategoryId(s.subcategory_id),
              parent_subcategory_id: normalizeSubcategoryId(s.parent_subcategory_id),
            }))
          );
        }
      } catch (e) {
        console.error("❌ Error fetching subcategories:", e);
        if (!cancelled) setSubcategories([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [viewUsername]);

  // ───────────────── Load items (always all; filter client-side) ────────────
  useEffect(() => {
    if (!viewUsername) return;
    let cancelled = false;

    const fetchItems = async () => {
      setLoading(true);
      setPrivateAccount(false);
      try {
        const { data } = await api.get(
          `/api/mycollection/${encodeURIComponent(viewUsername)}`
        );

        if (!cancelled) {
        const rawItems = Array.isArray(data) ? data : data?.items || [];
        const items = rawItems.map((item) => {
          const price = getPriceDollars(item);
          return {
            ...item,
            price,
            subcategory_id:
              item.subcategory_id != null
                  ? Number(item.subcategory_id)
                  : null,
            };
          });
          const profileData = !Array.isArray(data)
            ? (
                data?.viewed_user ||
                data?.user ||
                {
                  user_id: data?.user_id,
                  username: data?.username,
                  name: data?.name,
                  display_name: data?.display_name,
                  profile_picture: data?.profile_picture,
                  avatar_url: data?.avatar_url,
                }
              )
            : null;
          console.log("PUBLIC COLLECTION USER PROFILE", profileData);
          setViewedUser(profileData);
          setCollection(items);
          setCollectionSummary(data?.collection_summary || null);
        }
      } catch (err) {
        console.error("❌ Error fetching collection:", err);
        if (!cancelled) {
          setPrivateAccount(err?.response?.status === 403 && err?.response?.data?.error === "private_account");
          setCollection([]);
          setCollectionSummary(null);
          setViewedUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchItems();
    return () => {
      cancelled = true;
    };
  }, [viewUsername]);

  // ───────────────── Paging + filters ─────────────────
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedSubcategory, viewUsername, sortItemOption]);
  useEffect(() => { if (!showPrices && ["high", "low"].includes(sortItemOption)) setSortItemOption("az"); }, [showPrices, sortItemOption]);

  // Use same naming as search: prefer item_name, fallback to name
  const getDisplayName = (item) =>
    item.item_name || item.name || "Unnamed Item";

  const getSubtitle = (item) => {
    const categoryLabel = normalizeDisplayLabel(item.category);
    const seen = new Set();
    return [item.item_brand, item.item_type]
      .map((value) => String(value ?? "").trim())
      .filter((value) => {
        const normalized = normalizeDisplayLabel(value);
        if (!normalized || normalized === categoryLabel || seen.has(normalized)) return false;
        seen.add(normalized);
        return true;
      })
      .join(" • ");
  };

  const getFilteredTotalValue = () =>
    filteredCollection
      .reduce(
        (acc, item) => acc + (getPriceDollars(item) || 0),
        0
      );

  const normalizeSub = (val) =>
    val === "all" || val === "" || val === null || typeof val === "undefined"
      ? null
      : Number(val);

  // Filter by display name AND subcategory (if selected)
  const filteredCollection = collection.filter((item) => {
    const searchableText = [
      getDisplayName(item),
      item.brand,
      item.category,
      item.subcategory_name,
      item.parent_subcategory_name,
      item.player,
      item.set_name,
      item.edition_variant,
      item.number,
      item.year_released,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const matchesText = searchableText.includes(searchQuery.trim().toLowerCase());
    const matchesSub =
      subcategoryScopeIds === null ||
      subcategoryScopeIds.includes(Number(item.subcategory_id));
    const matchesStatus = statusFilter === "all" || statusActive(item, statusFilter);
    return matchesText && matchesSub && matchesStatus;
  });

  // Sort items – price, name, or date
  const sortedCollection = [...filteredCollection].sort((a, b) => {
    const priceA = getPriceDollars(a) ?? 0;
    const priceB = getPriceDollars(b) ?? 0;
    const dateA = new Date(a.added_on ?? a.date_added ?? a.created_at);
    const dateB = new Date(b.added_on ?? b.date_added ?? b.created_at);
    const nameA = getDisplayName(a);
    const nameB = getDisplayName(b);

    switch (sortItemOption) {
      case "high":
        return priceB - priceA;
      case "low":
        return priceA - priceB;
      case "az":
        return nameA.localeCompare(nameB);
      case "za":
        return nameB.localeCompare(nameA);
      case "newest":
        return dateB - dateA;
      case "oldest":
        return dateA - dateB;
      default:
        return 0;
    }
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedCollection.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.max(1, Math.ceil(sortedCollection.length / itemsPerPage));
  const selectedCount = selectedOwnershipIds.size;
  const visibleOwnershipIds = sortedCollection.map((item) => Number(item.user_item_id)).filter((id) => Number.isInteger(id) && id > 0);
  const selectedRows = collection.filter((item) => selectedOwnershipIds.has(Number(item.user_item_id)));
  const selectedStatusState = (key) => { const values = selectedRows.map((item) => statusActive(item, key)); return values.length && values.every(Boolean) ? "All" : values.length && values.every((value) => !value) ? "None" : "Mixed"; };
  const toggleOwnershipSelection = (id) => setSelectedOwnershipIds((previous) => { const next = new Set(previous); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const selectVisibleOwnerships = () => setSelectedOwnershipIds((previous) => new Set([...previous, ...visibleOwnershipIds]));
  const clearOwnershipSelection = () => setSelectedOwnershipIds(new Set());
  const exitSelectionMode = () => { setSelectionMode(false); clearOwnershipSelection(); };
  const bulkStatus = async (status, enabled) => {
    const selectedRows = collection.filter((item) => selectedOwnershipIds.has(Number(item.user_item_id)));
    if (enabled && status === "locked" && selectedRows.some((item) => statusActive(item, "for_trade") || statusActive(item, "for_sale")) && !window.confirm("Locking these items will remove them from For Trade and For Sale.")) return;
    setBulkBusy(true); setBulkNotice("");
    try { await api.patch("/api/mycollection/bulk/status", { user_item_ids: [...selectedOwnershipIds], status, enabled }); await refreshCollection(); setOrganizeAnchor(null); setBulkNotice("Status updated."); }
    catch (error) { setBulkNotice(error.response?.data?.locked_count ? `${error.response.data.locked_count} selected item(s) are Locked. Unlock them before marking For Trade or For Sale.` : "Unable to update selected statuses."); }
    finally { setBulkBusy(false); }
  };
  const refreshCollection = async () => { const response = await api.get(`/api/mycollection/${encodeURIComponent(viewUsername)}`); setCollection(response.data.items || []); setCollectionSummary(response.data.collection_summary || null); };
  const bulkSetQuantity = async () => {
    const value = window.prompt("Set quantity for every selected ownership row:", "1");
    if (value === null) return;
    const quantity = Number(value);
    if (!Number.isInteger(quantity) || quantity < 1) { setBulkNotice("Quantity must be at least 1 and a whole number."); return; }
    setBulkBusy(true); setBulkNotice("");
    try { await api.patch("/api/mycollection/bulk/quantity", { user_item_ids: [...selectedOwnershipIds], quantity }); await refreshCollection(); clearOwnershipSelection(); setBulkNotice("Quantity updated."); }
    catch (error) { setBulkNotice(error.response?.data?.error === "ownership_rows_not_owned_or_missing" ? "Some selected items are no longer in your collection." : "Unable to update collection."); }
    finally { setBulkBusy(false); }
  };
  const bulkRemove = async () => {
    if (!window.confirm(`Remove ${selectedCount} items from your collection? This does not delete catalog items.`)) return;
    setBulkBusy(true); setBulkNotice("");
    try { await api.post("/api/mycollection/bulk/remove", { user_item_ids: [...selectedOwnershipIds] }); await refreshCollection(); clearOwnershipSelection(); setBulkNotice("Selected items removed."); }
    catch (error) { setBulkNotice(error.response?.data?.error === "ownership_rows_not_owned_or_missing" ? "Some selected items are no longer in your collection." : "Unable to remove selected items."); }
    finally { setBulkBusy(false); }
  };

  const renderSubcategoryMenuItems = (nodes = [], depth = 0) =>
    nodes.flatMap((sub) => [
      <MenuItem key={sub.subcategory_id} value={String(sub.subcategory_id)}>
        <Box sx={{ pl: depth * 2 }}>{sub.name}</Box>
      </MenuItem>,
      ...(sub.children?.length
        ? renderSubcategoryMenuItems(sub.children, depth + 1)
        : []),
    ]);

  // ───────────────── Avatar / header display ─────────────────
  const normalizeHandle = (value) => String(value || "").trim().toLowerCase();
  const loggedHandles = [
    currentUser?.username,
    currentUser?.name,
    currentUser?.id,
  ].map(normalizeHandle).filter(Boolean);

  const displayMatch =
    viewUsername &&
    loggedHandles.includes(normalizeHandle(viewUsername));
  const isOwnCollection = Boolean(displayMatch);
  const hasActiveFilters =
    Boolean(searchQuery.trim()) || normalizeSub(selectedSubcategory) !== null || statusFilter !== "all";
  const showFirstCollectionOnboarding =
    isOwnCollection && collection.length === 0 && !hasActiveFilters;

  const currentUserAvatar =
    currentUser?.profile_picture ||
    currentUser?.profile_picture_url ||
    currentUser?.profilePictureUrl ||
    currentUser?.avatar_url ||
    currentUser?.image_url ||
    "";
  const viewedUserAvatar = viewedUser?.profile_picture || viewedUser?.avatar_url || "";

  const avatarSrc =
    displayMatch
      ? currentUserAvatar || "/logos/default-avatar.jpg"
      : viewedUserAvatar
      ? viewedUserAvatar
      : "/logos/default-avatar.jpg";
  const collectionOwnerLabel =
    viewedUser?.display_name ||
    viewedUser?.name ||
    viewedUser?.username ||
    viewUsername;

  if (!routeUsername && !(currentUser?.username || currentUser?.name)) {
    return (
      <Box
        sx={{
          minHeight: "50vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
          textAlign: "center",
        }}
      >
        <Typography variant="h6">Please log in to view your collection.</Typography>
        <Button variant="contained" onClick={() => navigate("/login")}>
          Go to Login
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", padding: "100px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (privateAccount) {
    return <Box sx={{ maxWidth: 640, mx: "auto", p: 4, textAlign: "center" }}><Typography variant="h5" fontWeight={800}>This profile is private</Typography><Typography color="text.secondary" sx={{ mt: 1 }}>Only the collector, accepted friends, and administrators can view this collection.</Typography></Box>;
  }

  return (
    <Box sx={{ maxWidth: 1200, margin: "auto", padding: showFirstCollectionOnboarding ? { xs: 2, sm: 3, md: 4 } : 4 }}>
      <Snackbar open={Boolean(bulkNotice)} autoHideDuration={2600} onClose={() => setBulkNotice("")} message={bulkNotice} />
      {/* Header + stats */}
      <Paper
        sx={{
          padding: { xs: 1.5, sm: 2 },
          marginBottom: 3,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.05fr) minmax(112px, .7fr) minmax(0, 1.8fr) 160px" },
          gridTemplateAreas: { xs: '"identity" "stats" "controls" "action" "selection"', md: '"identity stats controls action" "selection selection selection selection"' },
          alignItems: "center",
          gap: { xs: 1.25, md: 1.5 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            minWidth: 0,
            width: { xs: "100%", md: "auto" },
            flexDirection: { xs: "column", sm: "row" },
            textAlign: { xs: "center", sm: "left" },
            gridArea: "identity",
          }}
        >
          <Avatar
            src={avatarSrc}
            sx={{ width: { xs: 72, sm: 72 }, height: { xs: 72, sm: 72 }, mx: { xs: "auto", sm: 0 }, flexShrink: 0 }}
            imgProps={{
              referrerPolicy: "no-referrer",
              onError: (e) => {
                e.currentTarget.src = "/logos/default-avatar.jpg";
              },
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h1" variant={showFirstCollectionOnboarding ? "h4" : "h6"} sx={{ minWidth: 0, whiteSpace: { xs: "normal", sm: "nowrap" }, overflowWrap: "normal", wordBreak: "normal", color: showFirstCollectionOnboarding ? "#10233f" : "inherit", fontWeight: showFirstCollectionOnboarding ? 800 : "inherit" }}>
              {showFirstCollectionOnboarding ? `Welcome, ${collectionOwnerLabel}!` : `@${collectionOwnerLabel}'s Collection`}
            </Typography>
            {showFirstCollectionOnboarding && (
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>Let’s add the first item to your collection.</Typography>
            )}
            {!showFirstCollectionOnboarding && viewedUser?.username && viewedUser.username !== collectionOwnerLabel && (
              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                @{viewedUser.username}
              </Typography>
            )}
          </Box>
        </Box>

        {currentUser && viewUsername === (currentUser.username || currentUser.name) && Number(currentUser.is_admin || 0) === 1 && (
          <Box sx={{ gridArea: "action", display: "grid", gridTemplateColumns: "1fr", gap: .75, justifySelf: { xs: "stretch", md: "end" }, width: { xs: "100%", md: 150 }, p: .5, border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}>
            <Button variant="outlined" onClick={() => navigate("/mycollection/import")} sx={{ minHeight: 38, px: 1, fontWeight: 700, whiteSpace: "nowrap", borderRadius: .75, fontSize: "0.78rem" }}>
              Import Collection
            </Button>
            <Button variant="outlined" onClick={(event) => setShareAnchor(event.currentTarget)} sx={{ minHeight: 38, px: 1, fontWeight: 700, whiteSpace: "nowrap", borderRadius: .75, fontSize: "0.78rem" }}>
              Share Collection
            </Button>
            <Button variant={selectionMode ? "contained" : "outlined"} disabled={selectionMode} onClick={() => setSelectionMode(true)} sx={{ minHeight: 38, px: 1, fontWeight: 700, whiteSpace: "nowrap", borderRadius: .75, fontSize: "0.78rem" }}>
              Select Items
            </Button>
            <Menu anchorEl={shareAnchor} open={Boolean(shareAnchor)} onClose={() => setShareAnchor(null)}>
              <MenuItem onClick={copyCollectionLink}>Copy Collection Link</MenuItem>
              <MenuItem onClick={downloadCollectionPdf} disabled={pdfLoading}>{pdfLoading ? "Preparing PDF…" : "Download PDF"}</MenuItem>
            </Menu>
            <Snackbar
              open={pdfLoading}
              message={(
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <CircularProgress size={16} color="inherit" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>Preparing your PDF...</Typography>
                    <Typography variant="caption" sx={{ display: "block", lineHeight: 1.25 }}>This may take a few seconds for larger collections.</Typography>
                  </Box>
                </Box>
              )}
            />
            <Snackbar open={Boolean(shareNotice)} autoHideDuration={2200} onClose={() => setShareNotice("")} message={shareNotice} />
          </Box>
        )}

        {selectionMode && <Box sx={{ gridArea: "selection", display: "flex", alignItems: "center", flexWrap: "wrap", gap: .75, pt: 1.25, mt: .25, borderTop: "1px solid", borderColor: "divider" }}>
          <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap", mr: .5 }}>{selectedCount} selected</Typography>
          <Button size="small" variant="outlined" onClick={selectVisibleOwnerships} disabled={!visibleOwnershipIds.length}>Select All</Button>
          <Button size="small" onClick={bulkSetQuantity} disabled={!selectedCount || bulkBusy}>Set Quantity</Button>
          <Button size="small" onClick={(event) => setOrganizeAnchor(event.currentTarget)} disabled={!selectedCount || bulkBusy}>Organize</Button>
          <Button size="small" onClick={clearOwnershipSelection} disabled={!selectedCount}>Clear</Button>
          <Button size="small" onClick={exitSelectionMode}>Done</Button>
          <Box sx={{ ml: { xs: 0, md: "auto" }, pl: { md: 1 }, borderLeft: { md: "1px solid" }, borderColor: "divider" }}>
            <Button size="small" color="error" onClick={bulkRemove} disabled={!selectedCount || bulkBusy}>Remove</Button>
          </Box>
          <Menu anchorEl={organizeAnchor} open={Boolean(organizeAnchor)} onClose={() => setOrganizeAnchor(null)}>
            <Box sx={{ width: { xs: 285, sm: 360 }, p: 1.25 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Organize {selectedCount} Items</Typography>
              {OWNERSHIP_STATUS_OPTIONS.map(([key, text]) => (
                <Box key={key} sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto auto", alignItems: "center", gap: .75, py: .55, borderTop: "1px solid", borderColor: "divider" }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 0 }}>{text}</Typography>
                  <Chip size="small" label={selectedStatusState(key)} variant="outlined" sx={{ height: 22, fontSize: "0.7rem", borderRadius: "999px" }} />
                  <Box sx={{ display: "flex", gap: .4 }}>
                    <Button size="small" onClick={() => bulkStatus(key, true)} aria-label={`${key === "locked" ? "Lock" : "Add"} ${text}`}>{key === "locked" ? "Lock" : "Add"}</Button>
                    <Button size="small" onClick={() => bulkStatus(key, false)} aria-label={`${key === "locked" ? "Unlock" : "Remove"} ${text}`}>{key === "locked" ? "Unlock" : "Remove"}</Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </Menu>
        </Box>}

        <Box sx={{ gridArea: "stats", textAlign: showFirstCollectionOnboarding ? "center" : { xs: "left", md: "center" }, minWidth: 0 }}>
          {showFirstCollectionOnboarding ? (
            <Typography variant="body2" sx={{ color: "#284766", fontWeight: 700 }}>0 items tracked</Typography>
          ) : <>
          {(searchQuery.trim() || normalizeSub(selectedSubcategory) !== null) && (
            <Typography variant="caption" color="text.secondary">
              Showing stats for current filter
            </Typography>
          )}
          <Typography variant="body2">
            <strong>Total Items:</strong> {filteredCollection.length}
          </Typography>
          {!hasActiveFilters && collectionSummary?.delta && <Typography variant="caption" sx={{ display: "block", color: collectionSummary.delta.items >= 0 ? "success.main" : "error.main", fontWeight: 700 }}>
            {collectionSummary.delta.items > 0 ? "+" : ""}{collectionSummary.delta.items} today
          </Typography>}
          {showPrices && <Typography variant="body2">
            <strong>Total Value:</strong> {formatPrice({ price: getFilteredTotalValue().toFixed(2) }) || "TBD"}
          </Typography>}
          {showPrices && !hasActiveFilters && collectionSummary?.delta && <Typography variant="caption" sx={{ display: "block", color: collectionSummary.delta.value >= 0 ? "success.main" : "error.main", fontWeight: 700 }}>
            {collectionSummary.delta.value >= 0 ? "+" : "-"}{formatPrice({ price: Math.abs(collectionSummary.delta.value).toFixed(2) })} today{collectionSummary.delta.value_percent == null ? "" : ` (${collectionSummary.delta.value_percent > 0 ? "+" : ""}${collectionSummary.delta.value_percent}%)`}
          </Typography>}
          </>}
        </Box>

        {/* Search / filters */}
        {collection.length > 0 && <Box
          sx={{
            gridArea: "controls",
            display: "grid",
            gridTemplateRows: { xs: "auto", sm: "auto auto" },
            gap: 1,
            gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr)" },
            width: "100%",
          }}
        >
          {/* Search box */}
          <FormControl sx={{ flex: "1 1 50%", minWidth: { xs: "100%", sm: 240 } }}>
            <input
              type="text"
              placeholder="Search Collection..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: "16.5px 14px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                fontSize: "1rem",
                width: "100%",
                height: "56px",
                boxSizing: "border-box",
              }}
            />
          </FormControl>

          {/* Subcategory + sort controls (same style as ProfitLoss/Manage) */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
              gap: 1,
              minWidth: 0,
            }}
          >
          <FormControl sx={{ minWidth: 0, width: "100%" }}>
              <InputLabel>Subcategory</InputLabel>
              <Select
                value={selectedSubcategory}
                label="Subcategory"
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedSubcategory(v === "all" ? "all" : String(v));
                }}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value="all">Subcategory (All)</MenuItem>
                {renderSubcategoryMenuItems(subcategoryTree)}
              </Select>
          </FormControl>
          {isOwnCollection && <FormControl sx={{ minWidth: 0, width: "100%" }} size="small">
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(event) => { setStatusFilter(event.target.value); setCurrentPage(1); }}>
              <MenuItem value="all">All</MenuItem><MenuItem value="favorite">Favorites</MenuItem><MenuItem value="for_trade">For Trade</MenuItem><MenuItem value="for_sale">For Sale</MenuItem><MenuItem value="locked">Locked</MenuItem>
            </Select>
          </FormControl>}

            <FormControl sx={{ minWidth: 0, width: "100%" }}>
              <InputLabel>Sort Items</InputLabel>
              <Select
                value={sortItemOption}
                label="Sort Items"
                onChange={(e) => handleSortPreferenceChange(e.target.value)}
                MenuProps={{ disableScrollLock: true }}
              >
                {showPrices && <MenuItem value="high">Highest Value</MenuItem>}
                {showPrices && <MenuItem value="low">Lowest Value</MenuItem>}
                <MenuItem value="az">Name A–Z</MenuItem>
                <MenuItem value="za">Name Z–A</MenuItem>
                <MenuItem value="newest">Newest</MenuItem>
                <MenuItem value="oldest">Oldest</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>}
      </Paper>

      {/* Items grid */}
      {showFirstCollectionOnboarding ? (
        <FirstCollectionOnboarding navigate={navigate} />
      ) : currentItems.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: "center" }}>
          <Typography color="text.secondary">
            {collection.length > 0 && hasActiveFilters
              ? "No items match your current search or filter."
              : "This collector has not added any items yet."}
          </Typography>
          {collection.length > 0 && hasActiveFilters && (
            <Button variant="outlined" onClick={() => { setSearchQuery(""); setSelectedSubcategory("all"); setCurrentPage(1); }} sx={{ mt: 2, minHeight: 44 }}>
              Clear Filters
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {currentItems.map((item, index) => {
            const displayName = getDisplayName(item);
            const ownershipId = Number(item.user_item_id);
            const cardSelected = selectionMode && selectedOwnershipIds.has(ownershipId);
            const ebayQueryText =
              cleanField(item?.item_name) || cleanField(item?.name) || displayName;
            const subtitle = getSubtitle(item);
            const priceConfidence = normalizeConfidence(
              item.price_confidence ||
                item.pricing_confidence ||
                item.confidence ||
                item.priceConfidence ||
                item.pricingConfidence
            );
            const priceLastUpdated = formatDate(item.price_last_updated);
            const addedDate = item.added_on
              ? new Date(item.added_on).toLocaleDateString()
              : item.created_at
              ? new Date(item.created_at).toLocaleDateString()
              : "Unknown";
            const soldUrl = withEpnTracking(
              withEbaySoldCompleted(
                buildEbaySearchUrl(ebayQueryText, { includeSoldCompleted: true })
              ),
              `mycollection_sold_${item?.user_item_id ?? item?.item_id ?? index}`
            );
            const shopUrl = toShopEbayUrl(item, ebayQueryText, index);
            const showMeta = Boolean(priceLastUpdated || priceConfidence || addedDate || soldUrl || shopUrl);

            return (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={`${item.user_item_id ?? item.item_id}-${index}`}
              >
                <Card
                  role={selectionMode ? "button" : undefined}
                  tabIndex={selectionMode ? 0 : undefined}
                  aria-selected={selectionMode ? cardSelected : undefined}
                  onClick={(event) => {
                    if (!selectionMode || event.target.closest("button, a, input, select, textarea")) return;
                    toggleOwnershipSelection(ownershipId);
                  }}
                  onKeyDown={(event) => {
                    if (!selectionMode || (event.key !== "Enter" && event.key !== " ")) return;
                    if (event.target !== event.currentTarget) return;
                    event.preventDefault();
                    toggleOwnershipSelection(ownershipId);
                  }}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    cursor: selectionMode ? "pointer" : "default",
                    border: "2px solid",
                    borderColor: cardSelected ? "primary.main" : "transparent",
                    bgcolor: cardSelected ? "rgba(0, 137, 123, 0.06)" : "background.paper",
                    boxShadow: cardSelected ? "0 0 0 2px rgba(0, 137, 123, 0.12)" : undefined,
                    transition: "border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease",
                    ...(selectionMode && !cardSelected ? { "&:hover": { borderColor: "primary.light", bgcolor: "rgba(0, 137, 123, 0.025)" } } : {}),
                    ...(selectionMode ? { "&:focus-visible": { outline: "3px solid", outlineColor: "primary.main", outlineOffset: 2 } } : {}),
                  }}
                >
                  {selectionMode && <Box sx={{ position: "absolute", zIndex: 2, bgcolor: "background.paper", borderRadius: 1 }}><Checkbox checked={cardSelected} onClick={(event) => event.stopPropagation()} onChange={() => toggleOwnershipSelection(ownershipId)} inputProps={{ "aria-label": `Select ${displayName}` }} /></Box>}
                  <CardMedia
                    component="img"
                    height="200"
                    image={getDisplayImageUrl(item)}
                    alt={displayName}
                    sx={{
                      objectFit: "contain",
                      backgroundColor: "#f8f8f8",
                    }}
                  />
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {/* Name like search page */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 600,
                        lineHeight: 1.2,
                      }}
                      title={displayName}
                    >
                      {displayName}
                    </Typography>
                    {item.quantity != null && <Typography variant="body2" color="text.secondary">Quantity: {item.quantity}</Typography>}

                    {/* Brand / type line */}
                    {subtitle && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        {subtitle}
                      </Typography>
                    )}

                    <Box sx={{ minHeight: 56, display: "flex", flexDirection: "column", justifyContent: "flex-start", width: "100%", overflow: "hidden" }}>
                      {item.category && <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.35, minHeight: 20, overflowWrap: "anywhere" }}>{item.category}</Typography>}
                      {renderSubcategoryChips(item)}
                    </Box>
                    {isOwnCollection && <Box sx={{ minHeight: 26, display: "flex", gap: .5, flexWrap: "wrap", alignItems: "flex-start" }} aria-label="Ownership statuses">
                      {OWNERSHIP_STATUS_OPTIONS.filter(([key]) => statusActive(item, key)).map(([key, text]) => <Chip key={key} size="small" label={text} variant="outlined" sx={{ height: 23, borderRadius: "999px", fontSize: "0.72rem", fontWeight: 700, ...statusChipSx[key] }} />)}
                    </Box>}

                    {showPrices && <Typography variant="body1" sx={{ color: "primary.light" }}>
                      Value: {formatPrice(item) || "TBD"}
                    </Typography>}

                    {showMeta && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: 0.8,
                        }}
                      >
                        {showPrices && priceConfidence && <PriceConfidenceRibbon confidence={priceConfidence} />}
                        {showPrices && priceLastUpdated && (
                          <Typography variant="body2" color="text.secondary">
                            Price last updated: {priceLastUpdated}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          Added: {addedDate}
                        </Typography>
                        {showPrices && soldUrl && (
                          <Link
                            component="button"
                            type="button"
                            onClick={() => openOutbound(item, "ebay_comps", soldUrl)}
                            underline="hover"
                            sx={{
                              fontSize: "0.85rem",
                              cursor: "pointer",
                              textAlign: "left",
                              padding: 0,
                              background: "transparent",
                              border: "none",
                            }}
                          >
                            View sold comps on eBay
                          </Link>
                        )}
                        {shopUrl && (
                          <Link
                            component="button"
                            type="button"
                            onClick={() => openOutbound(item, "ebay_shop", shopUrl)}
                            underline="hover"
                            sx={{
                              fontSize: "0.85rem",
                              cursor: "pointer",
                              textAlign: "left",
                              padding: 0,
                              background: "transparent",
                              border: "none",
                            }}
                          >
                            Shop on eBay
                          </Link>
                        )}
                      </Box>
                    )}

                    <Button
                      variant="contained"
                      color="info"
                      fullWidth
                      onClick={() => navigate(buildItemUrl(item))}
                      sx={{ mt: "auto" }}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Paging controls */}
      {collection.length > 0 && <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        sx={{ mt: 4 }}
        buttonSx={{
          maxWidth: isMobile ? "100%" : 120,
          whiteSpace: "nowrap",
        }}
      />}
    </Box>
  );
};

export default MyCollection;
